import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ProductDetailViewModel } from '@product-detail/models/product-detail-view-model';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { ModalService } from '@shared/services/modal/modal.service';
import { ToastService } from '@shared/services/toast/toast.service';
import { ProductAnalyticsService } from '@usf/ngrx-product';
import { SharePDPViaEmailModel } from '@usf/file-share-types/share-pdp/share-pdp-model';
import { PanAppState } from '@panamax/app-state';
import { take } from 'rxjs';
import { ActionsSubject, Store } from '@ngrx/store';
import { SHARE_PDP_EMAIL_ACTIONS, TRACE_CONTEXT } from '@usf/ngrx-file-share';
import { ofType } from '@ngrx/effects';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { TranslateService } from '@ngx-translate/core';
import {
  formatInventoryText,
  getBoxDimensionArray,
  getCostBreakDownArray,
  getManufacturerDetailsArray,
  getNutritionalsTable,
  getShippingDetailsArray,
  getStorageInfoArray,
  getTextFromDOMById,
} from '@product-detail/utils/product-detail-print.util';
import {
  getOrderedContextKeys,
  getPriorityOrders,
} from '@product-detail/utils/product-images.util';
import { getProductStatusPillsList } from '@shared/helpers/document-creation.helpers';
import {
  PDPArrays,
  PDPHeaders,
  PDPImages,
  PDPLabels,
  ShareEventActions,
} from '@usf/file-share-types';
import { ShareViaEmailModalComponent } from '@shared/components/share-via-email-modal/share-via-email-modal.component';

@Injectable({
  providedIn: 'root',
})
export class ProductDetailShareViaEmailService {
  emailTo = '';
  message = '';
  sendCopy = false;
  includeProductPrices = false;
  emailString = '';

  constructor(
    public loadingSpinnerService: LoadingSpinnerService,
    protected panAppState: PanAppState,
    private toastService: ToastService,
    private modalController: ModalController,
    private modalService: ModalService,
    private productAnalyticsService: ProductAnalyticsService,
    private actionSubject: ActionsSubject,
    private translateService: TranslateService,
    private store: Store,
  ) {}

  async showShareViaEmailModal(product: ProductDetailViewModel) {
    const documentName = this.generateDocumentName(product);

    this.modalService.setModalOptions(
      false,
      ModalWithNavComponent,
      {
        rootPage: ShareViaEmailModalComponent,
        rootPageParams: {
          documentName: documentName,
          shareAction: ShareEventActions.SharePDP,
        },
      },
      'share-via-email-modal-desktop',
      true,
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();
    this.productAnalyticsService.trackShareViaEmailPDPOpenModal();

    await modal.onDidDismiss().then((resp: any) => {
      if (
        !!resp?.data &&
        !!resp?.data?.emailData &&
        !!resp?.data?.emailString &&
        resp?.data?.emailData?.emailTo !== undefined &&
        resp?.data?.emailData?.message !== undefined &&
        resp?.data?.emailData?.sendCopy !== undefined &&
        resp?.data?.emailData?.includeProductPrices !== undefined
      ) {
        this.emailTo = resp?.data?.emailData?.emailTo;
        this.message = resp?.data?.emailData?.message;
        this.sendCopy = resp?.data?.emailData?.sendCopy;
        this.includeProductPrices = resp?.data?.emailData?.includeProductPrices;
        this.emailString = resp?.data?.emailString;
        this.shareViaEmailProductDetail(documentName, product);
      }
    });
  }

  async shareViaEmailProductDetail(
    documentName: string,
    product: ProductDetailViewModel,
  ) {
    this.loadingSpinnerService.createSpinnerModal()?.then(async () => {
      this.panAppState.user$.pipe(take(1)).subscribe(async user => {
        const sharePDPViaEmailModel: SharePDPViaEmailModel = {
          emailInformation: {
            ccAddresses: this.emailTo?.split(','),
            firstName: user?.firstName,
            lastName: user?.lastName,
            message: this.message,
            sendMeACopy: this.sendCopy,
            sharedBy: user?.email,
          },
          product: product,
          fileName: documentName,
          includeProductPrices: this.includeProductPrices,
          headers: this.getHeaders(),
          labels: this.getLabels(product),
          arrays: this.getArrays(product),
          images: await this.getImages(product),
        };
        this.store.dispatch(
          SHARE_PDP_EMAIL_ACTIONS.sharePDPViaEmail({
            data: sharePDPViaEmailModel,
            tracking: {
              tracing: {
                data: {
                  traceContext: TRACE_CONTEXT.sharePDPViaEmail,
                  isStartOfTrace: true,
                  isEndOfTrace: false,
                },
              },
            },
          }),
        );
        this.actionSubject
          .pipe(ofType(SHARE_PDP_EMAIL_ACTIONS.sharePDPViaEmailSuccess))
          .subscribe(data => {
            this.loadingSpinnerService.dismissSpinnerModal();
            this.toastService.presentToastMsg(
              this.translateService.instant('i18n.sharePDP.successMessage') +
                this.emailString,
              'green-toast',
              MessageTypeEnum.success,
              [],
            );
          });
        this.actionSubject
          .pipe(ofType(SHARE_PDP_EMAIL_ACTIONS.sharePDPViaEmailFail))
          .subscribe(data => {
            this.loadingSpinnerService.dismissSpinnerModal();
            this.toastService.presentToastMsg(
              this.translateService.instant('i18n.sharePDP.failMessage') +
                this.emailString,
              'red-toast',
              MessageTypeEnum.error,
              [],
            );
          });
        this.productAnalyticsService.trackShareViaEmailPDP();
      });
    });
  }

  private generateDocumentName(product: ProductDetailViewModel): string {
    const productNumber = product.summary.productNumber;
    const productDescription = this.extractProductDescription(
      product.summary.productDescLong,
    );
    const currentDate = this.formatDate(new Date());

    return `${productNumber}_ProductDetail_${productDescription}_${currentDate}.pdf`;
  }

  private extractProductDescription(description: string): string {
    const words = description.split(' ');
    const firstTwoWords = words.slice(0, 2).join(' ');
    const sanitizedDescription = firstTwoWords.replace(/[ ,]/g, '');
    if (sanitizedDescription.length <= 20) {
      return sanitizedDescription;
    } else {
      return sanitizedDescription.slice(0, 20);
    }
  }

  private formatDate(date: Date): string {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString();

    return `${month}${day}${year}`;
  }

  private getHeaders(): PDPHeaders {
    const productClaimsHeader =
      this.translateService.instant(
        'i18n.productDetail.additionalInfo.productClaims',
      ) || '';
    const shippingDetailsHeader =
      this.translateService.instant(
        'i18n.productDetail.shippingInfo.shippingDetails',
      ) || '';
    const storageInfoHeading =
      this.translateService.instant(
        'i18n.productDetail.shippingInfo.storageInformation',
      ) || '';
    const manufacturerDetailsHeading =
      this.translateService.instant(
        'i18n.productDetail.shippingInfo.manufacturerDetails',
      ) || '';
    const additionalInfoIngredients =
      this.translateService.instant(
        'i18n.productDetail.additionalInfo.ingredients',
      ) || '';

    if (!productClaimsHeader) {
      console.error(
        '[ShareViaEmailService]: productClaimsHeader is empty or undefined',
      );
    }
    if (!shippingDetailsHeader) {
      console.error(
        '[ShareViaEmailService]: shippingDetailsHeader is empty or undefined',
      );
    }
    if (!storageInfoHeading) {
      console.error(
        '[ShareViaEmailService]: storageInfoHeading is empty or undefined',
      );
    }
    if (!manufacturerDetailsHeading) {
      console.error(
        '[ShareViaEmailService]: manufacturerDetailsHeading is empty or undefined',
      );
    }
    if (!additionalInfoIngredients) {
      console.error(
        '[ShareViaEmailService]: additionalInfoIngredients is empty or undefined',
      );
    }

    return {
      productClaimsHeader,
      shippingDetailsHeader,
      storageInfoHeading,
      manufacturerDetailsHeading,
      additionalInfoIngredients,
    };
  }

  private getLabels(product: ProductDetailViewModel): PDPLabels {
    const disclaimerInformationFootnoteLabel =
      this.translateService.instant(
        'i18n.productDetail.productDetailDisclaimer.informationAllergen',
      ) || '';
    const disclaimerImportantInfoLabel =
      this.translateService.instant(
        'i18n.productDetail.productDetailDisclaimer.important',
      ) +
        ' ' +
        this.translateService.instant(
          'i18n.productDetail.productDetailDisclaimer.importantText',
        ) || '';
    const disclaimerAllergenInfoLabel =
      this.translateService.instant(
        'i18n.productDetail.productDetailDisclaimer.information',
      ) || '';
    const additionalInfoIngredients =
      this.translateService.instant(
        'i18n.productDetail.additionalInfo.ingredients',
      ) || '';
    const productDetailDescriptionBenefits =
      this.translateService.instant(
        'i18n.productDetail.productDetailDescription.benefits',
      ) || '';
    const productDetailDescriptionFreatures =
      this.translateService.instant(
        'i18n.productDetail.productDetailDescription.features',
      ) || '';
    const productDetailDescriptionOverview =
      this.translateService.instant(
        'i18n.productDetail.productDetailDescription.overview',
      ) || '';
    const boxDimensionsLabel =
      this.translateService.instant(
        'i18n.productDetail.productDetailDescription.boxDimensions',
      ) || '';
    const casesPrice =
      getTextFromDOMById(`pdp-cases-price-display-${product?.productNumber}`) ||
      '';
    const casesInventory =
      formatInventoryText(
        getTextFromDOMById(
          `pdp-cases-inventory-display-${product?.productNumber}`,
        ),
        this.translateService,
      ) || '';
    const eachesPrice =
      getTextFromDOMById(
        `pdp-eaches-price-display-${product?.productNumber}`,
      ) || '';
    const eachesInventory =
      formatInventoryText(
        getTextFromDOMById(
          `pdp-eaches-inventory-display-${product?.productNumber}`,
        ),
        this.translateService,
      ) || '';
    const originGrownHarvested =
      getTextFromDOMById(`pdp-grown-harvested-${product?.productNumber}`) || '';
    const originProcessed =
      getTextFromDOMById(`pdp-processed-${product?.productNumber}`) || '';
    const preparationInstructions =
      this.translateService.instant(
        'i18n.productDetail.additionalInfo.preparationInstructions',
      ) || '';
    const servingSuggestions =
      this.translateService.instant(
        'i18n.productDetail.additionalInfo.servingSuggestions',
      ) || '';
    const handlingInstructions =
      this.translateService.instant(
        'i18n.productDetail.additionalInfo.handlingInstructions',
      ) || '';

    if (!disclaimerInformationFootnoteLabel) {
      console.error(
        '[ShareViaEmailService]: disclaimerInformationFootnoteLabel is empty or undefined',
      );
    }
    if (!disclaimerImportantInfoLabel) {
      console.error(
        '[ShareViaEmailService]: disclaimerImportantInfoLabel is empty or undefined',
      );
    }
    if (!disclaimerAllergenInfoLabel) {
      console.error(
        '[ShareViaEmailService]: disclaimerAllergenInfoLabel is empty or undefined',
      );
    }
    if (!additionalInfoIngredients) {
      console.error(
        '[ShareViaEmailService]: additionalInfoIngredients is empty or undefined',
      );
    }
    if (!productDetailDescriptionBenefits) {
      console.error(
        '[ShareViaEmailService]: productDetailDescriptionBenefits is empty or undefined',
      );
    }
    if (!productDetailDescriptionFreatures) {
      console.error(
        '[ShareViaEmailService]: productDetailDescriptionFreatures is empty or undefined',
      );
    }
    if (!productDetailDescriptionOverview) {
      console.error(
        '[ShareViaEmailService]: productDetailDescriptionOverview is empty or undefined',
      );
    }
    if (!boxDimensionsLabel) {
      console.error(
        '[ShareViaEmailService]: boxDimensionsLabel is empty or undefined',
      );
    }
    if (!casesPrice) {
      console.error('[ShareViaEmailService]: casesPrice is empty or undefined');
    }
    if (!casesInventory) {
      console.error(
        '[ShareViaEmailService]: casesInventory is empty or undefined',
      );
    }
    if (!eachesPrice) {
      console.error(
        '[ShareViaEmailService]: eachesPrice is empty or undefined',
      );
    }
    if (!eachesInventory) {
      console.error(
        '[ShareViaEmailService]: eachesInventory is empty or undefined',
      );
    }
    if (!originGrownHarvested) {
      console.error(
        '[ShareViaEmailService]: originGrownHarvested is empty or undefined',
      );
    }
    if (!originProcessed) {
      console.error(
        '[ShareViaEmailService]: originProcessed is empty or undefined',
      );
    }
    if (!preparationInstructions) {
      console.error(
        '[ShareViaEmailService]: preparationInstructions is empty or undefined',
      );
    }
    if (!servingSuggestions) {
      console.error(
        '[ShareViaEmailService]: servingSuggestions is empty or undefined',
      );
    }
    if (!handlingInstructions) {
      console.error(
        '[ShareViaEmailService]: handlingInstructions is empty or undefined',
      );
    }

    return {
      disclaimerInformationFootnoteLabel,
      disclaimerImportantInfoLabel,
      disclaimerAllergenInfoLabel,
      additionalInfoIngredients,
      productDetailDescriptionBenefits,
      productDetailDescriptionFreatures,
      productDetailDescriptionOverview,
      boxDimensionsLabel,
      casesPrice,
      casesInventory,
      eachesPrice,
      eachesInventory,
      originGrownHarvested,
      originProcessed,
      preparationInstructions,
      servingSuggestions,
      handlingInstructions,
    };
  }

  private getArrays(product: ProductDetailViewModel): PDPArrays {
    const costBreakdownArray =
      getCostBreakDownArray(
        product?.productNumber,
        this.includeProductPrices,
      )?.filter(Boolean) || [];

    const boxDimensionArray =
      getBoxDimensionArray(product?.productNumber)?.filter(Boolean) || [];
    const shippingDetailsArray =
      getShippingDetailsArray(product?.productNumber)?.filter(Boolean) || [];
    const storageInfoArray =
      getStorageInfoArray(product?.productNumber)?.filter(Boolean) || [];
    const manufacturerDetailsArray =
      getManufacturerDetailsArray(product?.productNumber)?.filter(Boolean) ||
      [];

    if (costBreakdownArray.includes(undefined)) {
      console.error(
        '[ShareViaEmailService]: Undefined value found in costBreakdownArray',
        costBreakdownArray,
      );
    }
    if (boxDimensionArray.includes(undefined)) {
      console.error(
        '[ShareViaEmailService]: Undefined value found in boxDimensionArray',
        boxDimensionArray,
      );
    }
    if (shippingDetailsArray.includes(undefined)) {
      console.error(
        '[ShareViaEmailService]: Undefined value found in shippingDetailsArray',
        shippingDetailsArray,
      );
    }
    if (storageInfoArray.includes(undefined)) {
      console.error(
        '[ShareViaEmailService]: Undefined value found in storageInfoArray',
        storageInfoArray,
      );
    }
    if (manufacturerDetailsArray.includes(undefined)) {
      console.error(
        '[ShareViaEmailService]: Undefined value found in manufacturerDetailsArray',
        manufacturerDetailsArray,
      );
    }

    return {
      costBreakdownDomItemsArray: costBreakdownArray,
      boxDimensionDomItemsArray: boxDimensionArray,
      shippingDetailsDomItemsArray: shippingDetailsArray,
      storageInfoDomItemsArray: storageInfoArray,
      manufactureDetailsDomItemsArray: manufacturerDetailsArray,
    };
  }

  private async getImages(product: ProductDetailViewModel): Promise<PDPImages> {
    const nutritionFactsElement = document.getElementById(
      `pdp-nutritional-info-${product?.productNumber}`,
    );

    if (!nutritionFactsElement) {
      console.error(
        '[ShareViaEmailService]: Nutrition facts element not found for product:',
        product?.productNumber,
      );
    }

    const nutritionFactsImage = (await getNutritionalsTable(
      nutritionFactsElement,
      product?.productNumber,
    )) || {
      includeNutritionFacts: false,
      nutritionFactsImage: '',
      heightToWidthRatio: undefined,
    };

    const productImages = await this.getProductImagesContextKeys(product);
    const pillsImages = await getProductStatusPillsList(product).map(pill => {
      return { filename: pill ?? 'NotAvailable.png', version: 'V1' };
    });

    if (!productImages || productImages.length === 0) {
      console.error(
        '[ShareViaEmailService]: Product images are missing or empty for product:',
        product?.productNumber,
      );
    }

    if (!pillsImages || pillsImages.length === 0) {
      console.error(
        '[ShareViaEmailService]: Pills images are missing or empty for product:',
        product?.productNumber,
      );
    }

    return {
      nutritionFactsImage,
      productImages: productImages || [],
      pillsImages: pillsImages || [],
      logoImage: '',
      imageIcon: '',
    };
  }

  private async getProductImagesContextKeys(product: ProductDetailViewModel) {
    const orderedContextKeys = getOrderedContextKeys(
      product?.summary?.productAssets?.productImages || {},
      getPriorityOrders(product?.summary),
    );

    return orderedContextKeys;
  }
}
